import { useI18n } from '@ecom/i18n';
import { type UseThemeResult } from '@ecom/ui-kit';
import { Command } from 'cmdk';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { NAV_GROUPS } from './nav';

/**
 * Cmd/Ctrl+K palette — navigation (available screens only) and the theme toggle.
 *
 * `theme` is passed in from AppShell rather than called locally — see the
 * comment on AppShell's own `useTheme()` call for why a second, independent
 * instance here would desync from the topbar's toggle.
 */
export function CommandPalette({ theme: themeState }: { theme: UseThemeResult }): ReactElement {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useI18n();
  // `toggleTheme` is called via `themeState.toggleTheme()` rather than
  // destructured — it's a method-shorthand on `UseThemeResult`, and
  // destructuring it would detach it from `themeState` (unbound-method).
  const { theme } = themeState;

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((prev) => !prev);
      }
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const availableItems = NAV_GROUPS.flatMap((group) =>
    group.items.map((item) => ({ ...item, groupTitleKey: group.titleKey })),
  );

  return (
    <AnimatePresence>
      {open ? (
        <Command.Dialog
          open={open}
          onOpenChange={setOpen}
          label={t('merchant.commandPalette.label')}
          className="fixed inset-0 z-50"
        >
          <motion.div
            className="fixed inset-0 bg-ink/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setOpen(false);
            }}
          />
          <motion.div
            className="fixed inset-x-0 top-24 mx-auto w-[min(560px,92vw)] overflow-hidden rounded-2xl border border-line bg-surface shadow-popover"
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.16, ease: 'easeOut' }}
          >
            <Command.Input
              autoFocus
              placeholder={t('merchant.commandPalette.placeholder')}
              className="w-full border-b border-line bg-transparent px-4 py-3.5 text-sm text-ink outline-none placeholder:text-inkFaint"
            />
            <Command.List className="max-h-80 overflow-y-auto p-2">
              <Command.Empty className="px-3 py-6 text-center text-sm text-inkFaint">
                {t('merchant.commandPalette.empty')}
              </Command.Empty>

              <Command.Group
                heading={t('merchant.commandPalette.navigationGroup')}
                className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-inkFaint"
              >
                {availableItems.map((item) => (
                  <Command.Item
                    key={item.to}
                    onSelect={() => {
                      void navigate(item.to);
                      setOpen(false);
                    }}
                    className="flex cursor-pointer items-center justify-between rounded-md px-2.5 py-2 text-sm text-ink data-[selected=true]:bg-accentSoft data-[selected=true]:text-accentInk"
                  >
                    <span>{t(item.labelKey)}</span>
                    <span className="text-xs text-inkFaint">{t(item.groupTitleKey)}</span>
                  </Command.Item>
                ))}
              </Command.Group>

              <Command.Group
                heading={t('merchant.commandPalette.preferencesGroup')}
                className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-inkFaint"
              >
                <Command.Item
                  onSelect={() => {
                    themeState.toggleTheme();
                  }}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm text-ink data-[selected=true]:bg-accentSoft data-[selected=true]:text-accentInk"
                >
                  {theme === 'dark'
                    ? t('merchant.commandPalette.toggleThemeToLight')
                    : t('merchant.commandPalette.toggleThemeToDark')}
                </Command.Item>
              </Command.Group>
            </Command.List>
          </motion.div>
        </Command.Dialog>
      ) : null}
    </AnimatePresence>
  );
}
